import { z } from 'zod';
import { BootService } from '@/services/boot';
import { ServiceError } from '@/types/service';
import { DsaReport, DsaReportSchema, Result } from '@/types/common';
import * as Type from '@/types/client';
import { requestHandler, responseHandler } from '@/utilities/service';

export async function getClientById(id: number): Promise<Result<ServiceError, Type.Client[]>> {
  return requestHandler(
    BootService.axios.get(`/customers?customer=${id}`).then(responseHandler(Type.ClientSchema.array())),
  );
}

export async function getClientByEmail(email: string): Promise<Result<ServiceError, Type.Client[]>> {
  return requestHandler(
    BootService.axios
      .get(`/customers?email=${encodeURIComponent(email)}`)
      .then(responseHandler(Type.ClientSchema.array())),
  );
}

export async function getClientByPhoneNumber(phoneNumber: string): Promise<Result<ServiceError, Type.Client[]>> {
  return requestHandler(
    BootService.axios.get(`/customers?phoneNumber=${phoneNumber}`).then(responseHandler(Type.ClientSchema.array())),
  );
}

export async function getClientSiftScoreById(id: number): Promise<Result<ServiceError, Type.ClientSiftScore>> {
  return requestHandler(
    BootService.axios
      .get(`/customers/sift-score?id=${encodeURIComponent(id)}`)
      .then(responseHandler(Type.ClientSiftScoreSchema)),
  );
}

export async function getClientLastLoginAttempt(
  id: string,
): Promise<Result<ServiceError, Type.ClientLastLoginAttempt>> {
  return requestHandler(
    BootService.axios.get(`/customers/${id}/login-attempt`).then(responseHandler(Type.ClientLastLoginAttemptSchema)),
  );
}

export type UpdateEmailData = {
  oldEmail: string;
  newEmail: string;
};
export async function updateEmail(id: number, data: UpdateEmailData): Promise<Result<ServiceError, unknown>> {
  return requestHandler(BootService.axios.patch(`/customers/${id}/email`, data).then(responseHandler(z.unknown())));
}

export type GetPaidConfirmationCodeData = {
  email: string;
};
export async function triggerGetPaidManualVerification(clientId: string, data: GetPaidConfirmationCodeData) {
  return BootService.mutationAxios.post(`/customers/${clientId}/get-paid-confirmation-code`, data);
}

export async function getClientActionHistos(
  id: number,
  page: number,
  perPage: number,
): Promise<Type.ClientActionHistos> {
  const res = await BootService.axios.get<Type.ClientActionHistos>(
    `/customers/${id}/actions-bo?page=${page}&perPage=${perPage}`,
  );
  return res.data;
}

export async function getClientBadges(id: number): Promise<Result<ServiceError, Type.ClientBadges>> {
  return requestHandler(
    BootService.axios.get(`/customers/${id}/badges`).then(responseHandler(Type.ClientBadgesSchema)),
  );
}

export async function getClientSalesReports(customerId: string): Promise<Type.ClientSalesReport[]> {
  const res = await BootService.mutationAxios.get<Type.ClientSalesReport[]>(`/customers/${customerId}/sales-reports`);
  return res.data;
}

export const ClientConnectResponseSchema = z.object({
  redirectTo: z.string().nullish().optional(),
  sessionId: z.string().nullish().optional(),
  cookieDomain: z.string().nullish().optional(),
  cookieName: z.string().nullish().optional(),
});

export type ClientConnectResponse = z.infer<typeof ClientConnectResponseSchema>;

export async function clientConnect(
  id: number,
  clientType?: 'buyer' | 'seller',
): Promise<Result<ServiceError, ClientConnectResponse>> {
  return requestHandler(
    BootService.axios
      .post(`/customers/${id}/connect`, { clientType })
      .then(responseHandler(ClientConnectResponseSchema)),
  );
}

export async function getClientChatModerateStats(
  id: number,
): Promise<Result<ServiceError, Type.ClientChatModerateStats>> {
  return requestHandler(
    BootService.axios
      .get(`/customers/${id}/chat-moderate-stats`)
      .then(responseHandler(Type.ClientChatModerateStatsSchema)),
  );
}

export async function getClientCanceledSalesById(
  id: number,
  page: number,
  perPage: number,
): Promise<Type.ClientCanceledSales> {
  const res = await BootService.axios.get<Type.ClientCanceledSales>(
    `/customers/${id}/canceled-sales?page=${page}&perPage=${perPage}`,
  );
  return res.data;
}

export async function getDsaReportsByClientId(clientId: number): Promise<Result<ServiceError, DsaReport[]>> {
  return requestHandler(
    BootService.axios
      .get(`/dsa/reports?id_customer_reported=${clientId}`)
      .then(responseHandler(DsaReportSchema.array())),
  );
}

export async function getClientShippingFromAddress(
  customerId: string,
  clientShippingAddressId?: number | null,
): Promise<Result<ServiceError, { data?: unknown }>> {
  return requestHandler(
    BootService.axios
      .get(`/adser/form/address/${clientShippingAddressId}?context=cs360_shipping&client_id=${customerId}`)
      .then(responseHandler(z.object({ data: z.unknown() }))),
  );
}

export async function getClientStats(id: number): Promise<Result<ServiceError, Type.ClientStatsResponse>> {
  return requestHandler(
    BootService.axios.get(`/customers/${id}/sellerStats`).then(responseHandler(Type.ClientStatsSchema)),
  );
}

export async function removeCancellation(productId: number, clientId: number): Promise<Result<ServiceError, unknown>> {
  const payload = {
    productId,
    eventType: 'canceled',
    action: 'RETRACT',
  };
  return BootService.mutationAxios.patch(`/customers/${clientId}/seller-stats/events`, payload);
}

export async function shippedInTimeItem(productId: number, clientId: number): Promise<Result<ServiceError, unknown>> {
  const payload = {
    productId,
    eventType: 'shipped',
    action: 'AMEND_TIME',
  };
  return BootService.mutationAxios.patch(`/customers/${clientId}/seller-stats/events`, payload);
}

export async function deleteVerifiedPhone(clientId: string): Promise<Result<ServiceError, unknown>> {
  return BootService.mutationAxios.delete(`/customers/${clientId}/verified-phone-number`);
}

export async function getClientReliabilityStats(
  id: number,
): Promise<Result<ServiceError, Type.ClientReliabilityStats>> {
  return requestHandler(
    BootService.axios
      .get(`/customers/${id}/reliability-stats`)
      .then(responseHandler(Type.ClientReliabilityStatsSchema)),
  );
}

export async function getPersonaKyc(id: number, page: number, perPage: number): Promise<Type.PersonalKyc> {
  const res = await BootService.axios.get<Type.PersonalKyc>(
    `/customers/${id}/kyc-requests/histories?page=${page}&perPage=${perPage}`,
  );
  return res.data;
}
